// Moved from konfigurator-base.liquid
/**
 * Konfigurator Base (UI logic)
 *
 * Purpose:
 * - Centralizes all non-tracking behavior for the product configurator (gin/vodka)
 * - Manages step navigation, Teeinblue integration, gallery previews, and variant alternatives popup
 * - Exposes a stable window.* API used by Liquid markup and other assets
 *
 * Conventions:
 * - All public functions are attached to window.* and documented with JSDoc blocks directly above definitions
 * - Function parameters are explicit; avoid hidden state where possible
 * - No analytics/tracking here (handled separately in konfigurator-tracking.js)
 *
 * Sections:
 * 1) UI: Mobile buy buttons placement
 * 2) Teeinblue form + gallery handling
 * 3) Gallery step images helpers
 * 4) Properties management and validation
 * 5) Teeinblue integration helpers
 * 6) Progress indicator & session helpers
 * 7) Cart drawer helpers
 * 8) Mode detection utility
 * 9) Buy button interception (handler + interceptors)
 * 10) Bootstrap (DOMContentLoaded)
 * 11) Variant popup z-index fix
 * 12) Group initializers (radio/wacholder/alkohol/begleiter)
 * 13) Navigation (toggle/next/prev/final/back)
 * 14) Variant alternatives system (data loading, popup, selection)
 */

/**
 * @typedef {Object} Variant
 * @property {number|string} id - Variant ID
 * @property {string} [title]
 * @property {boolean} [available]
 * @property {number} [price] - Price in cents
 * @property {{ preview_image?: { src?: string } }} [featured_media]
 * @property {{ src?: string }} [image]
 * @property {string[]} [options] - Option values in order (e.g., size)
 */

/**
 * @typedef {Object} Product
 * @property {number|string} [id]
 * @property {string} [title]
 * @property {string} [product_type]
 * @property {string[]} [tags]
 * @property {Variant[]} variants
 */

/**
 * @typedef {Object} VariantPicker
 * @property {Product} [product] - Loaded product data
 * @property {Variant} [selectedVariant] - Currently selected variant
 * @property {(variantId: string|number) => void} [selectVariant] - Programmatic selection
 * @property {string} [productHandle]
 */


// Section 1) UI: Mobile buy buttons placement

/**
 * Move the product buy buttons into the mobile konfigurator nav on small screens.
 * Hides the original buy buttons container to avoid duplication.
 * @param {HTMLElement} root - Konfigurator root element
 * @param {string} mode - 'gin' | 'vodka'
 */
window.moveBuyButtonsToMobileNav = function(root, mode) {
	// Only on mobile devices (≤600px)
	if (window.innerWidth > 600) return;
	const finalStep = root.querySelector('.konfigurator-' + mode + '-final-step.active');
	if (!finalStep) return;
	const buyButtonsElement = document.querySelector('.product-info__buy-buttons.show');
	const mobileNav = finalStep.querySelector('.konfigurator-nav');
	if (buyButtonsElement && mobileNav && !mobileNav.querySelector('.product-info__buy-buttons')) {
		buyButtonsElement.setAttribute('data-original-parent', 'buy-buttons-container');
		mobileNav.appendChild(buyButtonsElement);
		buyButtonsElement.classList.add('mobile-nav-buy-buttons');
		const buyButtonsContainer = finalStep.querySelector('.product-info__buy-buttons-container');
		if (buyButtonsContainer) {
			buyButtonsContainer.style.display = 'none';
		}
	}
};

/**
 * Restore product buy buttons back to their original container from the mobile nav.
 * @param {HTMLElement} root - Konfigurator root element
 * @param {string} mode - 'gin' | 'vodka'
 */
window.restoreBuyButtonsFromMobileNav = function(root, mode) {
	const finalStep = root.querySelector('.konfigurator-' + mode + '-final-step');
	if (!finalStep) return;
	const mobileNavBuyButtons = finalStep.querySelector('.konfigurator-nav .product-info__buy-buttons');
	if (mobileNavBuyButtons) {
		const buyButtonsContainer = finalStep.querySelector('.product-info__buy-buttons-container');
		if (buyButtonsContainer) {
			buyButtonsContainer.appendChild(mobileNavBuyButtons);
			buyButtonsContainer.style.display = '';
			mobileNavBuyButtons.classList.remove('mobile-nav-buy-buttons');
			mobileNavBuyButtons.removeAttribute('data-original-parent');
		}
	}
};


// Section 2) Teeinblue form + gallery handling

/**
 * Move the Teeinblue form into or out of the personalize step container, and force gallery refreshes.
 * Also toggles a CSS class on the gallery to surface it during personalization.
 * @param {HTMLElement} root - Konfigurator root element
 * @param {string} mode - 'gin' | 'vodka'
 * @param {boolean} toPersonalize - true to move into personalize section, false to move back to original container
 */
window.moveTeeinblueForm = function(root, mode, toPersonalize) {
	if (!root || typeof root.querySelector !== 'function') {
		console.error('moveTeeinblueForm: Invalid root element', root);
		return;
	}
	
	const teeForm = document.querySelector('.tee-campaign-form-wrapper');
	const personalizeContainer = root.querySelector('#' + mode + '-personalisiere-form-container');
	const originalContainer = document.querySelector('.product-info');
	const teeGallery = document.querySelector('.tee-gallery-content');

	if (!teeForm || !personalizeContainer || !originalContainer) return;

	if (toPersonalize) {
		personalizeContainer.appendChild(teeForm);
		teeForm.style.display = '';

		if (teeGallery) {
			teeGallery.classList.add('show-in-personalize');

			// Force reflow and gallery initialization
			const forceGalleryRefresh = () => {
				// Force reflow
				teeGallery.style.display = 'none';
				teeGallery.offsetHeight; // Force reflow
				teeGallery.style.display = '';

				// Try multiple teeinblue refresh methods
				if (window.teeinblue) {
					if (window.teeinblue.refresh) {
						window.teeinblue.refresh();
					}
					if (window.initTeeinblueWithAlkohol) {
						window.initTeeinblueWithAlkohol();
					}
					if (window.teeinblue.reload) {
						window.teeinblue.reload();
					}
				}

				// Dispatch resize event to trigger any responsive handlers
				window.dispatchEvent(new Event('resize'));
			};

			// Try multiple times with different delays
			setTimeout(forceGalleryRefresh, 100);
			setTimeout(forceGalleryRefresh, 300);
			setTimeout(forceGalleryRefresh, 500);

			// Watch for when gallery content is actually loaded
			const galleryObserver = new MutationObserver((mutations) => {
				let hasContent = false;
				mutations.forEach((mutation) => {
					if (mutation.addedNodes.length > 0) {
						hasContent = true;
					}
				});

				if (hasContent) {
					forceGalleryRefresh();
					galleryObserver.disconnect();
				}
			});

			galleryObserver.observe(teeGallery, {
				childList: true,
				subtree: true
			});
		}
	} else {
		originalContainer.appendChild(teeForm);
		teeForm.style.display = 'none';
		if (teeGallery) {
			teeGallery.classList.remove('show-in-personalize');
		}
	}
};


// Section 3) Gallery step images helpers

/**
 * Show a specific step image inside the product gallery, hiding other steps.
 * Hides the step image layer entirely for personalize/final steps.
 * @param {number|string} stepNumber - Step number to display or 'final'
 * @param {number} PERSONALIZE_STEP - Personalize step index used to suppress overlay
 * @param {Object.<number,string|null>} stepImages - Map of step -> image URL (or null)
 */
window.showStepImageInGallery = function(stepNumber, PERSONALIZE_STEP, stepImages) {
	const productGallery = document.querySelector('product-gallery');
	if (!productGallery) return;

	// Hide step images for personalize step and final step (they use Teeinblue container)
	if (stepNumber === PERSONALIZE_STEP || stepNumber === 'final') {
		window.hideStepImageFromGallery();
		return;
	}

	// Get step image URL
	const imageUrl = window.getStepImageUrl(stepNumber, stepImages);
	if (!imageUrl) return;

	// Find or create konfigurator container inside product gallery
	let konfiguatorContainer = productGallery.querySelector('.konfigurator-step-container');
	if (!konfiguatorContainer) {
		konfiguatorContainer = document.createElement('div');
		konfiguatorContainer.className = 'konfigurator-step-container';
		konfiguatorContainer.style.cssText = `
			position: relative;
			width: 100%;
			aspect-ratio: 1;
			display: flex;
			align-items: center;
			justify-content: center;
			background: #f8f8f8;
			overflow: hidden;
		`;
		if (productGallery.firstElementChild) {
			productGallery.insertBefore(konfiguatorContainer, productGallery.firstElementChild);
		} else {
			productGallery.appendChild(konfiguatorContainer);
		}
	}

	// Ensure current step image exists; create on-demand if missing
	let stepImg = konfiguatorContainer.querySelector('img[data-step="' + stepNumber + '"]');
	if (!stepImg) {
		stepImg = document.createElement('img');
		stepImg.dataset.step = stepNumber;
		stepImg.style.cssText = `
			width: 100%;
			height: 100%;
			object-fit: contain;
			position: absolute;
			top: 0;
			left: 0;
			transition: opacity 0.1s ease;
			display: none;
			opacity: 0;
		`;
		stepImg.src = imageUrl;
		stepImg.alt = 'Konfigurator Step ' + stepNumber;
		stepImg.loading = 'eager';
		konfiguatorContainer.appendChild(stepImg);
	}

	// Show current, hide others
	const allImages = konfiguatorContainer.querySelectorAll('img[data-step]');
	allImages.forEach(img => {
		if (img.dataset.step === stepNumber.toString()) {
			img.style.display = 'block';
			img.style.opacity = '1';
		} else {
			img.style.display = 'none';
			img.style.opacity = '0';
		}
	});
};

/**
 * Preload all step images into an overlay container in the product gallery.
 * Shows the first step by default.
 * @param {number} TOTAL_STEPS - Total number of wizard steps
 * @param {Object.<number,string|null>} stepImages - Map of step -> image URL (or null)
 */
window.preloadStepImages = function(TOTAL_STEPS, stepImages) {
	const productGallery = document.querySelector('product-gallery');
	if (!productGallery) return;

	// Create konfigurator container if it doesn't exist
	let konfiguatorContainer = productGallery.querySelector('.konfigurator-step-container');
	if (!konfiguatorContainer) {
		konfiguatorContainer = document.createElement('div');
		konfiguatorContainer.className = 'konfigurator-step-container';
		konfiguatorContainer.style.cssText = `
			position: relative;
			width: 100%;
			aspect-ratio: 1;
			display: flex;
			align-items: center;
			justify-content: center;
			background: #f8f8f8;
			overflow: hidden;
		`;

		// Insert container into product gallery at the beginning
		if (productGallery.firstElementChild) {
			productGallery.insertBefore(konfiguatorContainer, productGallery.firstElementChild);
		} else {
			productGallery.appendChild(konfiguatorContainer);
		}
	}

	// Preload all step images
	for (let stepNum = 1; stepNum <= TOTAL_STEPS; stepNum++) {
		const imageUrl = window.getStepImageUrl(stepNum, stepImages);
		if (imageUrl) {
			const img = document.createElement('img');
			img.dataset.step = stepNum;
			img.style.cssText = `
				width: 100%;
				height: 100%;
				object-fit: contain;
				position: absolute;
				top: 0;
				left: 0;
				transition: opacity 0.1s ease;
				display: none;
				opacity: 0;
			`;
			img.src = imageUrl;
			img.alt = `Konfigurator Step ${stepNum}`;
			img.loading = 'eager';
			konfiguatorContainer.appendChild(img);
		}
	}

	// Show the first step image by default
	const firstImage = konfiguatorContainer.querySelector('img[data-step="1"]');
	if (firstImage) {
		firstImage.style.display = 'block';
		firstImage.style.opacity = '1';
	}
};

/**
 * Remove the konfigurator step overlay container from the gallery.
 */
window.hideStepImageFromGallery = function() {
	const productGallery = document.querySelector('product-gallery');
	if (productGallery) {
		const konfiguatorContainer = productGallery.querySelector('.konfigurator-step-container');
		if (konfiguatorContainer) {
			konfiguatorContainer.remove();
		}
	}
};

/**
 * Resolve the image URL for a given step.
 * @param {number} stepNumber - Step index
 * @param {Object.<number,string|null>} stepImages - Map of step -> image URL
 * @returns {string|null} image URL or null if none present
 */
window.getStepImageUrl = function(stepNumber, stepImages) {
	return stepImages[stepNumber] || null;
};


// Section 4) Properties management and validation

/**
 * Determine whether the selected recipe implies a bestseller (skips to personalize).
 * Uses the same logic as nextSection().
 * @param {HTMLElement} root - Konfigurator root element
 * @returns {boolean} true if bestseller or non-custom recipe selected
 */
window.isBestsellerSelected = function(root) {
	const selected = root.querySelector('input[name="properties[Rezept]"]:checked');
	if (!selected) return false;
	const val = (selected.value || '').toLowerCase();
	// Same logic as nextSection() - bestseller or anything NOT custom/own
	return val.includes('bestseller') || (!val.includes('eigenes') && !val.includes('custom') && !val.includes('own'));
};

/**
 * Clear konfigurator-related product properties when switching to a bestseller recipe.
 * Disables inputs to prevent submission, resets values, and normalizes Alkoholstärke to 40.
 * @param {HTMLElement} root - Konfigurator root element
 * @param {string} mode - 'gin' | 'vodka'
 * @param {string[]} KONFIGURATOR_PROPERTY_NAMES - Property names to clear (Wacholdernote/Hauptbotanical/Begleiter)
 */
window.clearKonfiguratorProperties = function(root, mode, KONFIGURATOR_PROPERTY_NAMES) {
	KONFIGURATOR_PROPERTY_NAMES.forEach(name => {
		// Find and disable property inputs
		const inputs = root.querySelectorAll(`[name="properties[${name}]"]`);
		inputs.forEach(input => {
			input.disabled = true;
			if (input.type === 'radio' || input.type === 'checkbox') {
				input.checked = false;
			} else {
				input.value = '';
			}
		});
	});

	// Clear begleiter hidden input
	const begleiterHidden = root.querySelector(`#${mode}_begleiter_hidden`);
	if (begleiterHidden) {
		begleiterHidden.disabled = true;
		begleiterHidden.value = '';
	}

	// Clear begleiter checkboxes
	const begleiterCheckboxes = root.querySelectorAll('input[name="begleiter_option"]');
	begleiterCheckboxes.forEach(cb => {
		cb.checked = false;
		cb.disabled = true;
	});

	// For bestseller we KEEP alkohol strength for label generation.
	// Ensure Teeinblue alkohol field is enabled and set to default 40.
	const hiddenAlk = document.querySelector('.hidden-alkoholstrenght-input input');
	if (hiddenAlk) {
		hiddenAlk.disabled = false;
		hiddenAlk.value = '40';
		// Trigger change/input so Teeinblue updates immediately
		try {
			hiddenAlk.dispatchEvent(new Event('input', { bubbles: true }));
			hiddenAlk.dispatchEvent(new Event('change', { bubbles: true }));
		} catch (e) {}
	}

	// Also normalize the UI state: select the first alkohol option (assumed 40%)
	try {
		const alkoholGroup = root.querySelector('.alkohol-bar-group');
		if (alkoholGroup) {
			const radios = alkoholGroup.querySelectorAll('input[type="radio"][name="properties[Alkoholstärke]"]');
			if (radios.length > 0) {
				// Clear previous selection
				radios.forEach(r => { r.checked = false; });
				const first = radios[0];
				first.checked = true;
				// Update visual selection class
				alkoholGroup.querySelectorAll('.alkohol-bar').forEach(el => el.classList.remove('selected'));
				const bar = first.closest('.alkohol-bar');
				if (bar) bar.classList.add('selected');
				// Inform listeners
				first.dispatchEvent(new Event('change', { bubbles: true }));
			}
		}
		// Sync to teeinblue helper if available
		if (window.populateTeeinblueAlkoholStrength) {
			window.populateTeeinblueAlkoholStrength();
		}
	} catch (e) {}
};

/**
 * Restore konfigurator-related product properties when switching back from bestseller.
 * Re-enables inputs so they can be edited and submitted.
 * @param {HTMLElement} root - Konfigurator root element
 * @param {string} mode - 'gin' | 'vodka'
 * @param {string[]} KONFIGURATOR_PROPERTY_NAMES - Property names to restore
 */
window.restoreKonfiguratorProperties = function(root, mode, KONFIGURATOR_PROPERTY_NAMES) {
	KONFIGURATOR_PROPERTY_NAMES.forEach(name => {
		const inputs = root.querySelectorAll(`[name="properties[${name}]"]`);
		inputs.forEach(input => {
			input.disabled = false;
		});
	});

	// Restore begleiter hidden input
	const begleiterHidden = root.querySelector(`#${mode}_begleiter_hidden`);
	if (begleiterHidden) {
		begleiterHidden.disabled = false;
	}

	// Restore begleiter checkboxes
	const begleiterCheckboxes = root.querySelectorAll('input[name="begleiter_option"]');
	begleiterCheckboxes.forEach(cb => {
		cb.disabled = false;
	});

	// Restore Teeinblue alkohol field
	const hiddenAlk = document.querySelector('.hidden-alkoholstrenght-input input');
	if (hiddenAlk) {
		hiddenAlk.disabled = false;
	}
};

/**
 * Validate Teeinblue name fields and image upload on the personalize step,
 * enabling/disabling the Next button accordingly. Also wires live listeners and observers.
 * @param {HTMLElement} root - Konfigurator root element
 * @param {string} mode - 'gin' | 'vodka'
 * @param {number} PERSONALIZE_STEP - Personalize step index
 */
window.validateTeeinblueName = function(root, mode, PERSONALIZE_STEP) {
	const personalizeSection = root.querySelector('.konfigurator-' + mode + '-section[data-section="' + PERSONALIZE_STEP + '"]');
	if (!personalizeSection) return;

	const nextButton = personalizeSection.querySelector('.konfigurator-nav__button--next');
	if (!nextButton) return;

	// Find the teeinblue name inputs and image upload
	const checkInput = () => {
		const nameInputs = document.querySelectorAll('.tee-field__input input.tee__input--text');
		const imageUploaded = document.querySelector('.tee-photo-uploaded');

		// Check first input field (required)
		const firstInputValid = nameInputs[0] && nameInputs[0].value.trim().length > 0;
		// Second input field is no longer required
		// Check image upload (required)
		const imageValid = !!imageUploaded;

		nextButton.disabled = !(firstInputValid && imageValid);

		// Also update alkohol strength when checking inputs
		if (window.populateTeeinblueAlkoholStrength) {
			window.populateTeeinblueAlkoholStrength(root);
		}

		// Add event listeners to first input field if not already added
		if (nameInputs[0] && !nameInputs[0].hasAttribute('data-has-validator')) {
			nameInputs[0].setAttribute('data-has-validator', 'true');
			nameInputs[0].addEventListener('input', checkInput);
			nameInputs[0].addEventListener('change', checkInput);
		}
		// Add event listeners to second input field (optional, but we still listen for changes)
		if (nameInputs[1] && !nameInputs[1].hasAttribute('data-has-validator')) {
			nameInputs[1].setAttribute('data-has-validator', 'true');
			nameInputs[1].addEventListener('input', checkInput);
			nameInputs[1].addEventListener('change', checkInput);
		}
		// For image upload, use MutationObserver to watch for changes
		if (!document.body.hasAttribute('data-image-observer')) {
			document.body.setAttribute('data-image-observer', 'true');
			const observer = new MutationObserver(checkInput);
			observer.observe(document.body, { childList: true, subtree: true });
		}
	};

	checkInput();

	// Watch for changes to the personalize section
	const sectionObserver = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			if (mutation.target.classList.contains('active')) {
				checkInput();
			}
		});
	});

	sectionObserver.observe(personalizeSection, {
		attributes: true,
		attributeFilter: ['class']
	});

	// Watch for the teeinblue form being added
	const formContainerObserver = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			if (mutation.addedNodes.length > 0) {
				checkInput();
			}
		});
	});

	const formContainer = root.querySelector('#' + mode + '-personalisiere-form-container');
	if (formContainer) {
		formContainerObserver.observe(formContainer, {
			childList: true,
			subtree: true
		});
	}
};


// Section 5) Teeinblue integration helpers

/**
 * Populate the hidden Teeinblue Alkoholstärke field based on current konfigurator selection.
 * Sets custom values for bestsellers: Classic=44, Exotic=40, Freestyle=43.5
 * @param {HTMLElement} root - Konfigurator root element (optional in some contexts)
 */
window.populateTeeinblueAlkoholStrength = function(root) {
	let alkoholValue = '40'; // Default value
	
	// Check if a bestseller is selected
	const isBestseller = window.isBestsellerSelected && window.isBestsellerSelected(root);
	
	if (isBestseller) {
		// Get the selected recipe to determine which bestseller
		const selectedRecipe = root ? root.querySelector('input[name="properties[Rezept]"]:checked') : 
		                              document.querySelector('input[name="properties[Rezept]"]:checked');
		
		if (selectedRecipe && selectedRecipe.value) {
			const recipeName = (selectedRecipe.value || '').toLowerCase();
			
			// Set custom alcohol strength based on bestseller type
			if (recipeName.includes('classic')) {
				alkoholValue = '44';
			} else if (recipeName.includes('exotic')) {
				alkoholValue = '40';
			} else if (recipeName.includes('freestyle')) {
				alkoholValue = '43.5';
			}
		}
	} else {
		// For custom recipes, get the selected alkohol strength from the konfigurator
		const selectedAlkohol = document.querySelector('input[name="properties[Alkoholstärke]"]:checked');

		if (selectedAlkohol && selectedAlkohol.value) {
			// Extract numeric value from the selected option (e.g., "38%" -> "38")
			const match = selectedAlkohol.value.match(/\d+/);
			if (match) {
				alkoholValue = match[0];
			}
		}
	}

	// Find the teeinblue alkohol input field (any input inside the hidden-alkoholstrenght-input container)
	const alkoholInput = document.querySelector('.hidden-alkoholstrenght-input input');
	if (alkoholInput) {
		alkoholInput.value = alkoholValue;

		// Trigger multiple events to ensure teeinblue recognizes the value change
		const events = ['input', 'change', 'keyup', 'blur'];
		events.forEach(eventType => {
			const event = new Event(eventType, { bubbles: true });
			alkoholInput.dispatchEvent(event);
		});

		// Also try jQuery events if jQuery is available
		if (window.jQuery && window.jQuery(alkoholInput).length) {
			window.jQuery(alkoholInput).trigger('change').trigger('input');
		}

		// Try to trigger teeinblue specific refresh/update methods
		setTimeout(() => {
			if (window.teeinblue) {
				// Try various teeinblue refresh methods
				if (window.teeinblue.updatePreview) {
					window.teeinblue.updatePreview();
				}
				if (window.teeinblue.refresh) {
					window.teeinblue.refresh();
				}
				if (window.teeinblue.reload) {
					window.teeinblue.reload();
				}
				if (window.teeinblue.renderPreview) {
					window.teeinblue.renderPreview();
				}
				// Force a complete re-render
				if (window.teeinblue.init) {
					window.teeinblue.init();
				}
			}
		}, 100);

		// Also try after a longer delay in case teeinblue needs more time
		setTimeout(() => {
			if (window.teeinblue && window.teeinblue.updatePreview) {
				window.teeinblue.updatePreview();
			}
		}, 500);
	}
};

/**
 * Initialize/refresh Teeinblue and then ensure Alkoholstärke is synchronized with the configurator.
 * Performs multiple retries to account for async mounts.
 * @param {HTMLElement} root - Konfigurator root element
 */
window.initTeeinblueWithAlkohol = function(root) {
	if (window.teeinblue && window.teeinblue.init) {
		window.teeinblue.init();

		// Wait for teeinblue to be fully loaded, then populate alkohol strength multiple times
		const populateWithRetry = (attempt = 1, maxAttempts = 5) => {
			if (window.populateTeeinblueAlkoholStrength) {
				window.populateTeeinblueAlkoholStrength(root);
			}

			// Check if the input field exists and has the right value
			const alkoholInput = document.querySelector('.hidden-alkoholstrenght-input input');
			if (alkoholInput && attempt < maxAttempts) {
				// Try again with increasing delays
				setTimeout(() => populateWithRetry(attempt + 1, maxAttempts), attempt * 300);
			}
		};

		// Start the retry process after teeinblue has had time to initialize
		setTimeout(() => populateWithRetry(), 300);

		// Also force a final update after everything should be loaded
		setTimeout(() => {
			if (window.populateTeeinblueAlkoholStrength) {
				window.populateTeeinblueAlkoholStrength(root);
			}
			// Force one final refresh of teeinblue
			if (window.teeinblue.updatePreview) {
				window.teeinblue.updatePreview();
			}
		}, 2000);
	}
};

// Section 6) Progress indicator & session helpers

/**
 * Update all progress bars to reflect the current step (active/completed states).
 * @param {HTMLElement} root - Konfigurator root element
 * @param {string} mode - 'gin' | 'vodka'
 * @param {number|string} currentStep - Step index or 'final'
 */
window.updateProgressIndicator = function(root, mode, currentStep) {
	const allProgressBars = root.querySelectorAll('.nav-progress');
	allProgressBars.forEach(progressBar => {
		const progressSteps = progressBar.querySelectorAll('.konfigurator-' + mode + '-progress__step');
		const progressConnectors = progressBar.querySelectorAll('.konfigurator-' + mode + '-progress__connector');
		progressSteps.forEach((step, index) => {
			const stepNumber = index + 1;
			step.classList.remove('active', 'completed');
			if (currentStep === 'final') {
				step.classList.add('completed');
			} else if (stepNumber < currentStep) {
				step.classList.add('completed');
			} else if (stepNumber === currentStep) {
				step.classList.add('active');
			}
		});
		progressConnectors.forEach((connector, index) => {
			const connectorStep = index + 1;
			connector.classList.remove('completed');
			if (currentStep === 'final') {
				connector.classList.add('completed');
			} else if (connectorStep < currentStep) {
				connector.classList.add('completed');
			}
		});
	});
};

/**
 * Initialize a session flag lifecycle for the upselling popup so it only shows once per page session.
 * Clears the flag on navigation events (visibilitychange/pagehide) and on initial load.
 * @param {string} mode - 'gin' | 'vodka'
 */
window.initUpsellingPopupSession = function(mode) {
	const popupShownKey = `upselling-popup-shown-${mode}-${window.location.pathname}`;

	// Clear session flag on page load (ensures fresh start on refresh/reload)
	sessionStorage.removeItem(popupShownKey);

	// Also clear flag when page becomes hidden (mobile browsers, tab switching, etc.)
	document.addEventListener('visibilitychange', function() {
		if (document.hidden) {
			sessionStorage.removeItem(popupShownKey);
		}
	});

	// Clear flag on page unload events (backup)
	window.addEventListener('pagehide', function() {
		sessionStorage.removeItem(popupShownKey);
	});
};


// Section 7) Cart drawer helpers (non-tracking)
(() => {
	let cartDrawerBlocked = false;
	let originalAddEventListener = null;

	// Prevents cart drawer from opening while a modal is active
	/**
	 * Temporarily prevent the cart drawer from opening while a modal (e.g., variant popup) is active.
	 * Replaces document.addEventListener for 'cart:item-added' and stubs cartDrawer.show.
	 */
	window.preventCartDrawerOpening = function() {
		if (cartDrawerBlocked) return;
		cartDrawerBlocked = true;

		const cartDrawer = document.getElementById('cart-drawer');
		if (cartDrawer && cartDrawer.hasAttribute('open') && typeof cartDrawer.hide === 'function') {
			try { cartDrawer.hide(); } catch (_) {}
		}

		// Intercept cart:item-added events to set blocking flag
		originalAddEventListener = document.addEventListener;
		document.addEventListener = function(type, listener, options) {
			if (type === 'cart:item-added') {
				const wrappedListener = function(event) {
					if (event.detail && !event.detail.blockCartDrawerOpening) {
						event.detail.blockCartDrawerOpening = true;
					}
					return listener.call(this, event);
				};
				return originalAddEventListener.call(this, type, wrappedListener, options);
			}
			return originalAddEventListener.call(this, type, listener, options);
		};

		// Block direct cart drawer show
		if (cartDrawer && cartDrawer.show) {
			cartDrawer._originalShow = cartDrawer.show;
			cartDrawer.show = function() { return Promise.resolve(); };
		}
	};

	// Restores cart drawer functionality
	/**
	 * Restore default cart drawer behavior after a modal is dismissed.
	 * Reinstates the original event listener and cartDrawer.show.
	 */
	window.restoreCartDrawerOpening = function() {
		if (!cartDrawerBlocked) return;
		cartDrawerBlocked = false;

		if (originalAddEventListener) {
			document.addEventListener = originalAddEventListener;
			originalAddEventListener = null;
		}

		const cartDrawer = document.getElementById('cart-drawer');
		if (cartDrawer && cartDrawer._originalShow) {
			cartDrawer.show = cartDrawer._originalShow;
			delete cartDrawer._originalShow;
		}
	};

	// Close variant alternatives popup helper
	/**
	 * Close the variant alternatives popup for a given mode and restore scroll/cart drawer.
	 * @param {string} mode - 'gin' | 'vodka'
	 */
	window.closeVariantAlternativesPopup = function(mode) {
		const popup = document.getElementById(`variant-alternatives-popup-${mode}`);
		if (popup) {
			popup.classList.remove('show');
			document.body.style.overflow = '';
			window.restoreCartDrawerOpening();
		}
	};
})();


/**
 * Check current variant selection and toggle visibility of test class.
 * Shows toHiddenClass when 500ml variant is selected, hides otherwise.
 * @param {VariantPicker|HTMLElement} variantPicker - The variant picker element
 */
window.checkVariantAndToggleVisibility = function(variantPicker) {
	var toHiddenClass = ".candyrack-ppp-offer-id-331904.candyrack-ppp-product-id-8291349758216.candyrack-ppp-checkbox-layout__offer.styles_offerWrapper__107pldj1";

	// Get variant picker if not provided
	if (!variantPicker) {
		variantPicker = window.currentVariantPicker || document.querySelector('variant-picker');
	}
	
	if (!variantPicker) return;

	// Get currently selected variant
	var selectedVariant = null;
	if (variantPicker.selectedVariant) {
		selectedVariant = variantPicker.selectedVariant;
	} else {
		// Fallback: check form input
		var form = document.querySelector('form[action*="/cart/add"]');
		var variantIdInput = form ? form.querySelector('input[name="id"], select[name="id"]') : null;
		if (variantIdInput && variantIdInput.value && variantPicker.product) {
			selectedVariant = variantPicker.product.variants.find(function(v) { 
				return v.id == variantIdInput.value; 
			});
		}
	}

	if (!selectedVariant) return;

	// Check if variant title contains "500ml" or if options contain "500ml"
	var is500ml = false;
	try {
		var title = (selectedVariant.title || '').toLowerCase();
		is500ml = title.includes('500ml') || title.includes('500 ml');

		// Also check options array if title check fails
		if (!is500ml && selectedVariant.options) {
			is500ml = selectedVariant.options.some(function(opt) {
				var optStr = (opt || '').toString().toLowerCase();
				return optStr.includes('500ml') || optStr.includes('500 ml');
			});
		}
	} catch (e) {}

	// Toggle visibility: use !important to override CSS display:none rule
	var testElements = document.querySelectorAll(toHiddenClass);
	testElements.forEach(function(el) {
		if (is500ml) {
			// Override CSS display:none with !important
			el.style.setProperty('display', 'flex', 'important');
		} else {
			// Keep hidden
			el.style.setProperty('display', 'none', 'important');
		}
	});
};

/**
 * Initialize variant visibility checker with listeners.
 * Monitors variant changes and updates toHiddenClass visibility automatically.
 */
window.initVariantVisibilityChecker = function() {
	var variantPicker = window.currentVariantPicker || document.querySelector('variant-picker');
	if (!variantPicker) {
		// Retry after delay if picker not found yet
		setTimeout(window.initVariantVisibilityChecker, 500);
		return;
	}

	// Run check function
	var runCheck = function() {
		try {
			window.checkVariantAndToggleVisibility(variantPicker);
		} catch (e) {
			// Silent catch to prevent breaking other functionality
		}
	};

	// Immediate check on init
	runCheck();
	
	// Poll for changes every 300ms (most reliable method)
	var lastVariantId = null;
	var lastVariantTitle = null;
	
	setInterval(function() {
		var currentVariant = variantPicker.selectedVariant;
		if (currentVariant) {
			var currentId = currentVariant.id;
			var currentTitle = currentVariant.title;
			if (currentId !== lastVariantId || currentTitle !== lastVariantTitle) {
				lastVariantId = currentId;
				lastVariantTitle = currentTitle;
				runCheck();
			}
		} else {
			// Also check form input as fallback
			var form = document.querySelector('form[action*="/cart/add"]');
			var variantIdInput = form ? form.querySelector('input[name="id"], select[name="id"]') : null;
			if (variantIdInput && variantIdInput.value && variantIdInput.value !== lastVariantId) {
				lastVariantId = variantIdInput.value;
				runCheck();
			}
		}
	}, 300);

	// Also listen to events as backup
	var eventHandler = function(event) {
		setTimeout(runCheck, 50);
	};

	// Listen for variant changes on document
	document.addEventListener('variant:change', eventHandler);

	// Listen to form changes
	var form = document.querySelector('form[action*="/cart/add"]');
	if (form) {
		form.addEventListener('variant:change', eventHandler);
		
		var variantIdInput = form.querySelector('input[name="id"], select[name="id"]');
		if (variantIdInput) {
			variantIdInput.addEventListener('change', eventHandler);
		}
	}

	// Listen to variant picker option changes
	if (variantPicker.querySelectorAll) {
		var optionInputs = variantPicker.querySelectorAll('input[type="radio"], select');
		optionInputs.forEach(function(input) {
			input.addEventListener('change', eventHandler);
		});
	}
};

// Section 8) Mode detection utility
/**
 * Detect the current konfigurator mode from DOM markers.
 * Falls back to 'gin' if none can be detected.
 * @returns {('gin'|'vodka')} detected mode
 */
function getKonfiguratorMode() {
	try {
		if (document.querySelector('.konfigurator-gin') || document.getElementById('variant-alternatives-popup-gin')) {
			return 'gin';
		}
		if (document.querySelector('.konfigurator-vodka') || document.getElementById('variant-alternatives-popup-vodka')) {
			return 'vodka';
		}
	} catch (_) {}
	return 'gin';
}

/**
 * Handles buy button clicks to show variant alternatives (upsell) before purchase
 * Mirrors the previous Liquid implementation, but derives mode from DOM
 * @param {MouseEvent} event - click event captured on add-to-cart buttons
 * @returns {Promise<boolean>} true to allow default flow, false when intercepted
 */
window.handleBuyButtonClick = async function(event) {
	try {
		// Mobile click throttling (1s)
		var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
		if (isMobile) {
			var lastClickTime = window.lastBuyButtonClick || 0;
			var currentTime = Date.now();
			if (currentTime - lastClickTime < 1000) {
				event.preventDefault();
				event.stopPropagation();
				return false;
			}
			window.lastBuyButtonClick = currentTime;
		}

		var mode = getKonfiguratorMode();
		var popupShownKey = 'upselling-popup-shown-' + mode + '-' + window.location.pathname;
		var popupAlreadyShown = sessionStorage.getItem(popupShownKey) === 'true';
		if (popupAlreadyShown) {
			return true;
		}

		// Resolve currently selected variant
		var form = event.target.closest('form[action*="/cart/add"]');
		var variantIdInput = form ? form.querySelector('input[name="id"], select[name="id"]') : null;
		if (variantIdInput && variantIdInput.value) {
			var product = await window.getProductData(window.currentVariantPicker, mode);
			if (product && product.variants && product.variants.length > 1) {
				var selectedVariant = product.variants.find(function(v) { return v.id == variantIdInput.value; });
				var otherVariants = product.variants.filter(function(v) { return v.id !== selectedVariant.id && v.available; });

				// Safety: skip upsell for null-null products in gin/vodka konfigurator
				if (mode === 'gin' || mode === 'vodka') {
					var productTitle = (product.title || '').toLowerCase();
					var isNullNullProduct = productTitle.includes('null') || productTitle.includes('alternative');
					if (isNullNullProduct) {
						return true;
					}
				}

				if (otherVariants.length > 0) {
					sessionStorage.setItem(popupShownKey, 'true');
					event.preventDefault();
					event.stopPropagation();
					window.showVariantAlternativesPopup(selectedVariant, window.currentVariantPicker, mode, true);
					return false;
				}
			}
		}
	} catch (_e) {
		// silent
	}
	// Allow normal flow if no interception happened
	return true;
};


// Section 9) Bootstrap (DOMContentLoaded)
/**
 * Auto-initialize konfigurator roots from data-* attributes set in Liquid markup.
 * Wires selection handlers, navigation, validators, Teeinblue, variant alternatives, and tracking attach.
 */
document.addEventListener('DOMContentLoaded', function() {
	var roots = document.querySelectorAll('.product-info__konfigurator');
	if (!roots || roots.length === 0) return;

	roots.forEach(function(root) {
		try {
			var mode = root.dataset.mode || getKonfiguratorMode();
			var isGin = mode === 'gin';
			var TOTAL_STEPS = parseInt(root.dataset.totalSteps || '5', 10);
			var PERSONALIZE_STEP = parseInt(root.dataset.personalizeStep || '5', 10);
			var ALKOHOL_STEP = parseInt(root.dataset.alkoholStep || '2', 10);
			var BOTANICAL_STEP = parseInt(root.dataset.botanicalStep || '3', 10);
			var BEGLEITER_STEP = parseInt(root.dataset.begleiterStep || '4', 10);

			// Step images from data-image-<index>
			var stepImages = {};
			for (var i = 1; i <= TOTAL_STEPS; i++) {
				var url = root.getAttribute('data-image-' + i);
				stepImages[i] = url && url.length ? url : null;
			}

			// Initialize selection handlers
			if (typeof window.initRadioGroups === 'function') window.initRadioGroups(mode, BOTANICAL_STEP);
			if (isGin && typeof window.initWacholderGroup === 'function') window.initWacholderGroup(mode, ALKOHOL_STEP);
			if (typeof window.initAlkoholBarGroup === 'function') window.initAlkoholBarGroup(root);
			if (typeof window.initBegleiterGroup === 'function') window.initBegleiterGroup(mode, BEGLEITER_STEP);

			// Enable/disable "Weiter" buttons based on selection
			root.addEventListener('change', function(event) {
				if (event.target && event.target.type === 'radio') {
					var section = event.target.closest('.konfigurator-' + mode + '-section');
					if (!section) return;
					var nextButton = section.querySelector('.konfigurator-nav__button--next');
					if (!nextButton) return;

					if (isGin && section.dataset.section == ALKOHOL_STEP) {
						var wacholderSelected = section.querySelector('input[name="properties[Wacholdernote]"]:checked');
						var alkoholSelected = section.querySelector('input[name="properties[Alkoholstärke]"]:checked');
						nextButton.disabled = !(wacholderSelected && alkoholSelected);
					} else {
						var radioGroup = section.querySelector('.radio-group');
						var selected = null;
						if (radioGroup) {
							selected = radioGroup.querySelector('input[type="radio"]:checked');
						} else {
							var alkoholBarGroup = section.querySelector('.alkohol-bar-group');
							if (alkoholBarGroup) selected = alkoholBarGroup.querySelector('input[type="radio"]:checked');
						}
						nextButton.disabled = !selected;
					}
				}
			});

			// Begleiter validation
			var begleiterSection = root.querySelector('.konfigurator-' + mode + '-section[data-section="' + BEGLEITER_STEP + '"]');
			if (begleiterSection) {
				var begleiterNextButton = begleiterSection.querySelector('.konfigurator-nav__button--next');
				if (begleiterNextButton) begleiterNextButton.disabled = false;
				begleiterSection.addEventListener('change', function(event) {
					if (event.target && event.target.type === 'checkbox') {
						if (typeof window.validateCheckboxGroup === 'function') {
							window.validateCheckboxGroup(event.target, mode);
						}
					}
				});
			}

			// Initialize Begleiter hidden input once (if at least one checkbox exists)
			var checkboxes = root.querySelectorAll('input[name="begleiter_option"]');
			if (checkboxes && checkboxes.length > 0 && typeof window.validateCheckboxGroup === 'function') {
				window.validateCheckboxGroup(checkboxes[0], mode);
			}

			// Delegated nav actions
			root.addEventListener('click', function(event) {
				var button = event.target.closest('[data-action]');
				if (!button) return;
				var action = button.dataset.action;
				var section = button.dataset.section ? parseInt(button.dataset.section, 10) : null;
				if (action === 'nextSection' && section !== null) {
					window.nextSection(section, root, mode, TOTAL_STEPS, PERSONALIZE_STEP, stepImages);
				} else if (action === 'previousSection' && section !== null) {
					window.previousSection(section, root, mode, PERSONALIZE_STEP, stepImages);
				} else if (action === 'showFinalStep') {
					window.showFinalStep(root, mode);
				} else if (action === 'backToPersonalize') {
					window.backToPersonalize(root, mode, PERSONALIZE_STEP, stepImages);
				}
			});

			// Auto-clear/restore properties on recipe change
			var KONFIGURATOR_PROPERTY_NAMES = ['Wacholdernote','Hauptbotanical','Begleiter'];
			var recipeRadios = root.querySelectorAll('input[name="properties[Rezept]"]');
			recipeRadios.forEach(function(radio) {
				radio.addEventListener('change', function() {
					if (typeof window.isBestsellerSelected === 'function' && window.isBestsellerSelected(root)) {
						if (typeof window.clearKonfiguratorProperties === 'function') {
							window.clearKonfiguratorProperties(root, mode, KONFIGURATOR_PROPERTY_NAMES);
						}
					} else {
						if (typeof window.restoreKonfiguratorProperties === 'function') {
							window.restoreKonfiguratorProperties(root, mode, KONFIGURATOR_PROPERTY_NAMES);
						}
					}
				});
			});

			// Safety net before submit
			var productForm = document.querySelector('form[action*="/cart/add"]');
			if (productForm) {
				productForm.addEventListener('submit', function() {
					if (typeof window.isBestsellerSelected === 'function' && window.isBestsellerSelected(root)) {
						if (typeof window.clearKonfiguratorProperties === 'function') {
							window.clearKonfiguratorProperties(root, mode, KONFIGURATOR_PROPERTY_NAMES);
						}
					}
				}, true);
			}

			// Validate personalize step name
			if (typeof window.validateTeeinblueName === 'function') {
				window.validateTeeinblueName(root, mode, PERSONALIZE_STEP);
				setTimeout(function(){ window.validateTeeinblueName(root, mode, PERSONALIZE_STEP); }, 1000);
			}

			// Resize: move/restore buy buttons on final step
			window.addEventListener('resize', function() {
				var finalStep = root.querySelector('.konfigurator-' + mode + '-final-step.active');
				if (!finalStep) return;
				if (window.innerWidth <= 600) {
					if (typeof window.moveBuyButtonsToMobileNav === 'function') window.moveBuyButtonsToMobileNav(root, mode);
				} else {
					if (typeof window.restoreBuyButtonsFromMobileNav === 'function') window.restoreBuyButtonsFromMobileNav(root, mode);
				}
			});

			// Initial setup
			if (typeof window.moveTeeinblueForm === 'function') window.moveTeeinblueForm(root, mode, false);
			if (typeof window.preloadStepImages === 'function') window.preloadStepImages(TOTAL_STEPS, stepImages);

			// Init variant alternatives and session helpers
			if (typeof window.initVariantAlternatives === 'function') window.initVariantAlternatives(mode);
			if (typeof window.initUpsellingPopupSession === 'function') window.initUpsellingPopupSession(mode);
			if (typeof window.initVariantPopupZIndexFix === 'function') window.initVariantPopupZIndexFix();

			// Initialize variant visibility checker
			if (typeof window.initVariantVisibilityChecker === 'function') {
				window.initVariantVisibilityChecker();
			}

			// Tracking attach
			if (window.KonfiguratorTracking && typeof window.KonfiguratorTracking.attach === 'function') {
				window.KonfiguratorTracking.attach({
					root: root,
					mode: mode,
					totalSteps: TOTAL_STEPS,
					personalizeStep: PERSONALIZE_STEP,
					isGin: isGin
				});
			}
		} catch (e) {
			// silent
		}
	});
});


// Section 10) Variant popup z-index fix
/**
 * Maintain header z-index while the variant alternatives popup is shown.
 * Observes all popups whose id starts with 'variant-alternatives-popup-'.
 */
window.initVariantPopupZIndexFix = function() {
		var header = document.getElementById('shopify-section-header');
		if (!header) return;

		var popups = document.querySelectorAll('[id^="variant-alternatives-popup-"], .variant-alternatives-popup');
		if (!popups || popups.length === 0) return;

		popups.forEach(function(popup) {
			try {
				var update = function() {
					if (popup.classList.contains('show')) {
						header.style.zIndex = '0';
					} else {
						header.style.zIndex = '10';
					}
				};
				// Set initial state
				update();
				var observer = new MutationObserver(function() { update(); });
				observer.observe(popup, { attributes: true, attributeFilter: ['class'] });
			} catch (e) {}
		});
	};

/**
 * Initialize radio group event handlers for recipe selection and botanicals
 * @param {string} mode - 'gin' or 'vodka'
 * @param {number} stepBotanical - Step number for botanical selection
 */
window.initRadioGroups = function(mode, stepBotanical) {
	var radioGroups = document.querySelectorAll(
		'.konfigurator-' + mode + '-section[data-section="1"] .radio-group, '
		+ '.konfigurator-' + mode + '-section[data-section="' + stepBotanical + '"] .radio-group'
	);
	radioGroups.forEach(function(group) {
		if (!group) return;
		var boxes = group.querySelectorAll('.rezept-box');
		boxes.forEach(function(box) {
			box.addEventListener('click', function(e) {
				boxes.forEach(function(b) { b.classList.remove('selected'); });
				box.classList.add('selected');
				var input = box.querySelector('input[type="radio"]');
				if (input) input.checked = true;
				input.dispatchEvent(new Event('change', { bubbles: true }));
				// Debug log: show selected recipe value and label
				var value = input ? input.value : null;
				// .rezept-box-title is a sibling, not a child
				var labelElem = box.parentElement.querySelector('.rezept-box-title');
				var label = labelElem ? labelElem.innerText : null;
			});
			box.addEventListener('keydown', function(e) {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					box.click();
				}
			});
		});
		var checked = group.querySelector('input[type="radio"]:checked');
		if (checked) {
			var selectedBox = checked.closest('.rezept-box');
			if (selectedBox) selectedBox.classList.add('selected');
		}
	});
};

/**
 * Initialize Variant Alternatives system (JS orchestrator only)
 * - Finds the variant-picker element with retries
 * - Exposes it on window.currentVariantPicker
 * - Delegates wiring to window.setupVariantChangeListener if present,
 *   otherwise attaches buy button interceptors as a fallback
 * This function intentionally avoids any tracking and does not render UI.
 * @param {string} [mode] - Optional mode hint ('gin' or 'vodka') for scoped queries
 */
window.initVariantAlternatives = function(mode) {
	const tryFind = () => {
		let variantPicker = null;

		// 1) Any variant-picker with a form attribute
		variantPicker = document.querySelector('variant-picker[form]');

		// 2) Fallback: any variant-picker on the page
		if (!variantPicker) {
			variantPicker = document.querySelector('variant-picker');
		}

		// 3) Final step container scoping
		if (!variantPicker && mode) {
			const finalStep = document.querySelector('.konfigurator-' + mode + '-final-step');
			if (finalStep) {
				variantPicker = finalStep.querySelector('variant-picker');
			}
		}

		// 4) Product info section
		if (!variantPicker) {
			const productInfo = document.querySelector('.product-info');
			if (productInfo) {
				variantPicker = productInfo.querySelector('variant-picker');
			}
		}

		if (variantPicker) {
			// Expose globally for other helpers
			window.currentVariantPicker = variantPicker;

			// Delegate to Liquid-defined listener if available
			if (typeof window.setupVariantChangeListener === 'function') {
				try { window.setupVariantChangeListener(variantPicker); } catch (e) {}
			} else if (typeof window.setupBuyButtonInterceptors === 'function') {
				// Minimal fallback: ensure buy button interception works
				try {
					window.setupBuyButtonInterceptors();
					setTimeout(window.setupBuyButtonInterceptors, 1000);
					setTimeout(window.setupBuyButtonInterceptors, 3000);
				} catch (e) {}
			}
			return true;
		}
		return false;
	};

	if (tryFind()) return;

	// Retry a few times to allow dynamic content to mount
	let attempts = 0;
	const maxAttempts = 20; // ~10 seconds total
	const tick = () => {
		attempts++;
		if (tryFind()) return;
		if (attempts < maxAttempts) {
			setTimeout(tick, 500);
		}
	};
	setTimeout(tick, 500);
};

/**
 * Attach buy button interceptors that delegate to window.handleBuyButtonClick
 * Idempotent: removes existing listener before adding (capture phase)
 */
window.setupBuyButtonInterceptors = function() {
	if (typeof window.handleBuyButtonClick !== 'function') return;

	const selectors = [
		'button[name="add"]',
		'button[type="submit"]',
		'input[type="submit"]',
		'.btn[type="submit"]',
		'.button[type="submit"]',
		'.product-form__cart-submit',
		'.btn--add-to-cart',
		'[data-action="add-to-cart"]'
	].join(', ');

	const buttons = document.querySelectorAll(selectors);
	buttons.forEach((button) => {
		const form = button.closest('form[action*="/cart/add"]');
		if (!form) return;
		try {
			button.removeEventListener('click', window.handleBuyButtonClick, true);
		} catch (_) {}
		button.addEventListener('click', window.handleBuyButtonClick, true);
	});
};

/**
 * Initialize Wacholder note selection (Gin only)
 * @param {string} mode - 'gin' or 'vodka'
 * @param {number} stepAlkoholCombined - Combined alkohol/wacholder step number
 */
window.initWacholderGroup = function(mode, stepAlkoholCombined) {
	var wacholderGroup = document.querySelector('.konfigurator-' + mode + '-section[data-section="' + stepAlkoholCombined + '"] .radio-group');
	if (wacholderGroup) {
		var boxes = wacholderGroup.querySelectorAll('.rezept-box');
		boxes.forEach(function(box) {
			box.addEventListener('click', function(e) {
				boxes.forEach(function(b) { b.classList.remove('selected'); });
				box.classList.add('selected');
				var input = box.querySelector('input[type="radio"]');
				if (input) input.checked = true;
				input.dispatchEvent(new Event('change', { bubbles: true }));

				// Mobile smooth scroll to alkohol-bar-group on wacholder selection
				if (box.classList.contains('wacholder-option') && window.innerWidth <= 768) {
					setTimeout(function() {
						var alkoholBarGroup = document.querySelector('.alkohol-bar-group');
						if (alkoholBarGroup) {
							alkoholBarGroup.scrollIntoView({
								behavior: 'smooth',
								block: 'center'
							});
						}
					}, 300); // Small delay to allow selection animation to complete
				}
			});
			box.addEventListener('keydown', function(e) {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					box.click();
				}
			});
		});
		var checked = wacholderGroup.querySelector('input[type="radio"]:checked');
		if (checked) {
			var selectedBox = checked.closest('.rezept-box');
			if (selectedBox) selectedBox.classList.add('selected');
		}
	}
};

/**
 * Initialize Alkoholstärke bar selection
 * @param {HTMLElement} root - The konfigurator root element
 */
window.initAlkoholBarGroup = function(root) {
	var alkoholBarGroup = document.querySelector('.alkohol-bar-group');
	if (alkoholBarGroup) {
		var bars = alkoholBarGroup.querySelectorAll('.alkohol-bar');
		bars.forEach(function(bar) {
			bar.addEventListener('click', function(e) {
				bars.forEach(function(b) { b.classList.remove('selected'); });
				bar.classList.add('selected');
				var input = bar.querySelector('input[type="radio"]');
				if (input) input.checked = true;
				input.dispatchEvent(new Event('change', { bubbles: true }));

				// Update teeinblue alkohol strength if available
				if (window.populateTeeinblueAlkoholStrength && root && typeof root.querySelector === 'function') {
					setTimeout(() => window.populateTeeinblueAlkoholStrength(root), 100);
				}
			});
			bar.addEventListener('keydown', function(e) {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					bar.click();
				}
			});
		});
		var checked = alkoholBarGroup.querySelector('input[type="radio"]:checked');
		if (checked) {
			var selectedBar = checked.closest('.alkohol-bar');
			if (selectedBar) selectedBar.classList.add('selected');
		}
	}
};

/**
 * Initialize Begleiter botanicals checkbox selection
 * @param {string} mode - 'gin' or 'vodka'
 * @param {number} stepBegleiter - Step number for companion botanicals
 */
window.initBegleiterGroup = function(mode, stepBegleiter) {
	var begleiterGroup = document.querySelector('.konfigurator-' + mode + '-section[data-section="' + stepBegleiter + '"] .checkbox-group');
	if (begleiterGroup) {
		var options = begleiterGroup.querySelectorAll('.checkbox-option');
		options.forEach(function(option) {
			option.addEventListener('click', function(e) {
				var input = option.querySelector('input[type="checkbox"]');
				if (!input) return;
				var checkedCount = begleiterGroup.querySelectorAll('input[type="checkbox"]:checked').length;
				var max = parseInt(begleiterGroup.dataset.max);

				// If already max selected and trying to select another, block it
				if (!input.checked && checkedCount >= max) {
					// Show error on section
					begleiterGroup.closest('.konfigurator-' + mode + '-section').setAttribute('data-error', true);
					return;
				}
				// Toggle checked state
				input.checked = !input.checked;
				option.classList.toggle('selected', input.checked);
				begleiterGroup.closest('.konfigurator-' + mode + '-section').removeAttribute('data-error');
				input.dispatchEvent(new Event('change', { bubbles: true }));
			});
			option.addEventListener('keydown', function(e) {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					option.click();
				}
			});
		});
		// Preselect if already checked
		options.forEach(function(option) {
			var input = option.querySelector('input[type="checkbox"]');
			if (input && input.checked) {
				option.classList.add('selected');
			}
		});
	}
};

/**
 * Toggle konfigurator open/close
 * @param {HTMLElement} button - The toggle button clicked
 * @param {HTMLElement} root - Konfigurator root container
 * @param {string} mode - 'gin' or 'vodka'
 * @param {number} personalizeStep - Personalize step number
 * @param {Object} stepImages - Step image URLs map
 */
window.toggleKonfigurator = function(button, root, mode, personalizeStep, stepImages) {
	var container = button.closest('.konfigurator-' + mode);
	var options = container.querySelector('.konfigurator-' + mode + '-options');
	var firstSection = options.querySelector('.konfigurator-' + mode + '-section[data-section="1"]');
	
	if (!options.classList.contains('active')) {
		options.classList.add('active');
		firstSection.classList.add('active');
		window.updateProgressIndicator(root, mode, 1);
		window.showStepImageInGallery(1, personalizeStep, stepImages);
	} else {
		options.classList.remove('active');
		var allSections = options.querySelectorAll('.konfigurator-' + mode + '-section');
		allSections.forEach(function(section) { section.classList.remove('active'); });
		window.hideStepImageFromGallery();
	}
};

/**
 * Navigate to next section
 * @param {number} currentSection - Current section number
 * @param {HTMLElement} root - Konfigurator root
 * @param {string} mode - 'gin' or 'vodka'
 * @param {number} totalSteps - Total steps count
 * @param {number} personalizeStep - Personalize step number
 * @param {Object} stepImages - Step image URLs
 */
window.nextSection = function(currentSection, root, mode, totalSteps, personalizeStep, stepImages) {
	var container = root;
	var currentSectionEl = container.querySelector('.konfigurator-' + mode + '-section[data-section="' + currentSection + '"]');
	
	// Bestseller skip logic
	if (currentSection === 1) {
		var selectedRecipe = container.querySelector('input[name="properties[Rezept]"]:checked');
		if (selectedRecipe) {
			var value = selectedRecipe.value.toLowerCase();
			if (value.includes("bestseller") || (!value.includes("eigenes") && !value.includes("custom") && !value.includes("own"))) {
				var personalizeSection = container.querySelector('.konfigurator-' + mode + '-section[data-section="' + personalizeStep + '"]');
				if (personalizeSection) {
					currentSectionEl.classList.remove('active');
					personalizeSection.classList.add('active');
					
					if (window.innerWidth <= 768) {
						setTimeout(function() {
							var sectionTitle = personalizeSection.querySelector('.konfigurator-' + mode + '-section__title');
							if (sectionTitle) {
								sectionTitle.scrollIntoView({ behavior: 'smooth', block: 'start' });
							}
						}, 100);
					}
					
					window.updateProgressIndicator(root, mode, personalizeStep);
					window.showStepImageInGallery(personalizeStep, personalizeStep, stepImages);
					window.moveTeeinblueForm(root, mode, true);
					
					if (window.teeinblue && window.teeinblue.init) {
						setTimeout(function() {
							window.initTeeinblueWithAlkohol(root);
							window.validateTeeinblueName(root, mode, personalizeStep);
						}, 100);
					}
					return;
				}
			}
		}
	}
	
	// Normal progression
	var nextSectionEl = container.querySelector('.konfigurator-' + mode + '-section[data-section="' + (currentSection + 1) + '"]');
	if (nextSectionEl) {
		currentSectionEl.classList.remove('active');
		nextSectionEl.classList.add('active');

		if (window.innerWidth <= 768) {
			setTimeout(function() {
				var sectionTitle = nextSectionEl.querySelector('.konfigurator-' + mode + '-section__title');
				if (sectionTitle) {
					sectionTitle.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			}, 100);
		}

		window.updateProgressIndicator(root, mode, currentSection + 1);
		window.showStepImageInGallery(currentSection + 1, personalizeStep, stepImages);
		
		if (nextSectionEl.dataset.section == personalizeStep) {
			window.moveTeeinblueForm(root, mode, true);
			if (window.teeinblue && window.teeinblue.init) {
				setTimeout(function() {
					window.initTeeinblueWithAlkohol(root);
					window.validateTeeinblueName(root, mode, personalizeStep);
				}, 100);
			}
		}
	}
};

/**
 * Navigate to previous section
 * @param {number} currentSection - Current section number
 * @param {HTMLElement} root - Konfigurator root
 * @param {string} mode - 'gin' or 'vodka'
 * @param {number} personalizeStep - Personalize step number
 * @param {Object} stepImages - Step image URLs
 */
window.previousSection = function(currentSection, root, mode, personalizeStep, stepImages) {
	var container = root;
	var currentSectionEl = container.querySelector('.konfigurator-' + mode + '-section[data-section="' + currentSection + '"]');
	
	// Bestseller skip back logic
	if (currentSection == personalizeStep) {
		var selectedRecipe = container.querySelector('input[name="properties[Rezept]"]:checked');
		if (selectedRecipe) {
			var value = selectedRecipe.value.toLowerCase();
			if (value.includes("bestseller") || (!value.includes("eigenes") && !value.includes("custom") && !value.includes("own"))) {
				var recipeSection = container.querySelector('.konfigurator-' + mode + '-section[data-section="1"]');
				if (recipeSection) {
					currentSectionEl.classList.remove('active');
					recipeSection.classList.add('active');
					window.updateProgressIndicator(root, mode, 1);
					window.showStepImageInGallery(1, personalizeStep, stepImages);
					window.moveTeeinblueForm(root, mode, false);
					return;
				}
			}
		}
	}
	
	// Normal back
	var previousSectionEl = container.querySelector('.konfigurator-' + mode + '-section[data-section="' + (currentSection - 1) + '"]');
	if (previousSectionEl) {
		currentSectionEl.classList.remove('active');
		previousSectionEl.classList.add('active');
		window.updateProgressIndicator(root, mode, currentSection - 1);
		window.showStepImageInGallery(currentSection - 1, personalizeStep, stepImages);
		
		var isPersonalizeStep = previousSectionEl.dataset.section == personalizeStep;
		if (isPersonalizeStep) {
			window.moveTeeinblueForm(root, mode, true);
		} else {
			if (currentSection == personalizeStep) {
				window.moveTeeinblueForm(root, mode, false);
			}
		}
	}
};

/**
 * Show final checkout step
 * @param {HTMLElement} root - Konfigurator root
 * @param {string} mode - 'gin' or 'vodka'
 */
window.showFinalStep = function(root, mode) {
	var currentSection = root.querySelector('.konfigurator-' + mode + '-section.active');
	if (currentSection) {
		currentSection.classList.remove('active');
	}

	if (window.innerWidth <= 768) {
		setTimeout(function() {
			var finalSection = root.querySelector('.konfigurator-' + mode + '-final.active');
			var sectionTitle = finalSection ? finalSection.querySelector('.konfigurator-' + mode + '-section__title') : null;
			if (sectionTitle) {
				sectionTitle.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}, 100);
	}

	window.hideStepImageFromGallery();

	var teeGallery = document.querySelector('.tee-gallery-content');
	if (teeGallery) {
		teeGallery.classList.add('show-in-personalize');
	}

	var productBlocks = document.querySelectorAll('.product-info__variant-picker, .product-info__quantity-selector, .product-info__buy-buttons, .product-info__price, .product-info__text, .candyrack-ppp-checkbox-layout');
	productBlocks.forEach(function(block) { block.classList.add('show'); });

	var finalStep = root.querySelector('.konfigurator-' + mode + '-final-step');
	finalStep.classList.add('active');
	
	// Trigger variant visibility check when final step is shown
	if (typeof window.checkVariantAndToggleVisibility === 'function') {
		var variantPicker = window.currentVariantPicker || document.querySelector('variant-picker');
		if (variantPicker) {
			window.checkVariantAndToggleVisibility(variantPicker);
		}
	}
	window.moveBuyButtonsToMobileNav(root, mode);
	window.updateProgressIndicator(root, mode, 'final');

	// Initialize variant alternatives after final step is shown
	if (typeof window.initVariantAlternatives === 'function') {
		setTimeout(function() { window.initVariantAlternatives(mode); }, 1000);
	}
};

/**
 * Go back from final step to personalize
 * @param {HTMLElement} root - Konfigurator root
 * @param {string} mode - 'gin' or 'vodka'
 * @param {number} personalizeStep - Personalize step number
 * @param {Object} stepImages - Step image URLs
 */
window.backToPersonalize = function(root, mode, personalizeStep, stepImages) {
	var finalStep = root.querySelector('.konfigurator-' + mode + '-final-step');
	finalStep.classList.remove('active');
	
	window.restoreBuyButtonsFromMobileNav(root, mode);

	var productBlocks = document.querySelectorAll('.product-info__variant-picker, .product-info__quantity-selector, .product-info__buy-buttons, .product-info__price, .product-info__text, .candyrack-ppp-checkbox-layout');
	productBlocks.forEach(function(block) { block.classList.remove('show'); });

	var personalizeSection = root.querySelector('.konfigurator-' + mode + '-section[data-section="' + personalizeStep + '"]');
	personalizeSection.classList.add('active');

	if (window.innerWidth <= 768) {
		setTimeout(function() {
			var sectionTitle = personalizeSection.querySelector('.konfigurator-' + mode + '-section__title');
			if (sectionTitle) {
				sectionTitle.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}, 100);
	}

	window.updateProgressIndicator(root, mode, personalizeStep);
	window.showStepImageInGallery(personalizeStep, personalizeStep, stepImages);
	window.moveTeeinblueForm(root, mode, true);

	if (window.teeinblue && window.teeinblue.init) {
		setTimeout(function() { window.initTeeinblueWithAlkohol(root); }, 100);
	}
};

/**
 * Validate checkbox group selection
 * @param {HTMLInputElement} checkbox - The checkbox that was clicked
 * @param {string} mode - 'gin' or 'vodka'
 */
window.validateCheckboxGroup = function(checkbox, mode) {
	var group = checkbox.closest('.checkbox-group');
	var max = parseInt(group.dataset.max);
	var checked = group.querySelectorAll('input[type="checkbox"]:checked').length;
	var nextButton = group.closest('.konfigurator-' + mode + '-section').querySelector('.konfigurator-nav__button--next');
	
	if (nextButton) nextButton.disabled = false;
	
	if (checked > max) {
		checkbox.checked = false;
		group.closest('.konfigurator-' + mode + '-section').setAttribute('data-error', true);
	} else {
		group.closest('.konfigurator-' + mode + '-section').removeAttribute('data-error');
	}

	var selectedValues = Array.from(group.querySelectorAll('input[type="checkbox"]:checked')).map(function(input) { return input.value; }).join(', ');
	var hiddenInput = document.getElementById(mode + '_begleiter_hidden');
	if (hiddenInput) {
		hiddenInput.value = selectedValues;
	}
};

/**
 * VARIANT ALTERNATIVES SYSTEM
 * Functions for handling product variant alternatives and upselling
 */

/**
 * Sets up variant change listeners and buy button interception
 * @param {VariantPicker|HTMLElement} variantPicker - The variant picker element
 * @param {string} mode - 'gin' or 'vodka'
 */
window.setupVariantChangeListener = function(variantPicker, mode) {
	window.currentVariantPicker = variantPicker;
	
	var setupBuyButtonListeners = function() {
		if (window.setupBuyButtonInterceptors) {
			window.setupBuyButtonInterceptors();
		}
	};

	setupBuyButtonListeners();
	setTimeout(setupBuyButtonListeners, 1000);
	setTimeout(setupBuyButtonListeners, 3000);
	
	// Trigger visibility check for .varianten_tester
	if (typeof window.checkVariantAndToggleVisibility === 'function') {
		window.checkVariantAndToggleVisibility(variantPicker);
	}
};

/**
 * Triggers variant alternatives check and popup display
 * @param {VariantPicker|HTMLElement} variantPicker - The variant picker element
 * @param {string} mode - 'gin' or 'vodka'
 */
window.triggerVariantAlternativesCheck = async function(variantPicker, mode) {
	try {
		var selectedVariant = null;
		
		if (variantPicker && variantPicker.selectedVariant) {
			selectedVariant = variantPicker.selectedVariant;
		}
		
		if (!selectedVariant) {
			var form = document.querySelector('form[action*="/cart/add"]');
			var variantIdInput = form ? form.querySelector('input[name="id"], select[name="id"]') : null;
			if (variantIdInput && variantIdInput.value) {
				var product = await window.getProductData(variantPicker, mode);
				if (product && product.variants) {
					selectedVariant = product.variants.find(function(v) { return v.id == variantIdInput.value; });
				}
			}
		}
		
		if (selectedVariant) {
			window.showVariantAlternativesPopup(selectedVariant, variantPicker, mode, false);
		}
	} catch (error) {
		// Silently handle errors
	}
};

/**
 * Retrieves product data from various sources
 * @param {VariantPicker|HTMLElement} variantPicker - The variant picker element
 * @param {string} mode - 'gin' or 'vodka'
 * @returns {Promise<Product|null>} Product data object or null
 */
window.getProductData = async function(variantPicker, mode) {
	if (variantPicker && variantPicker.product) {
		return variantPicker.product;
	}
	
	var productHandle = variantPicker ? (variantPicker.getAttribute('handle') || variantPicker.productHandle) : null;
	if (!productHandle) {
		productHandle = window.location.pathname.split('/').pop();
	}
	
	if (productHandle) {
		productHandle = productHandle.replace(/[^a-z0-9\-]/gi, '').toLowerCase();
		
		var isGinVodkaKonfigurator = mode === 'gin' || mode === 'vodka';
		var isNullNullProduct = productHandle.includes('null') || productHandle.includes('alternative');
		
		if (isGinVodkaKonfigurator && isNullNullProduct) {
			var urlPath = window.location.pathname;
			var urlSegments = urlPath.split('/').filter(function(segment) { return segment.length > 0; });
			var productSegment = urlSegments[urlSegments.length - 1];
			
			if (productSegment && !productSegment.includes('null') && productSegment !== productHandle) {
				productHandle = productSegment;
			} else {
				return null;
			}
		}
	}
	
	if (!productHandle) {
		return null;
	}
	
	try {
		if (window.ProductLoader && window.ProductLoader.load) {
			return await window.ProductLoader.load(productHandle);
		}
		
		var response = await fetch('/products/' + productHandle + '.js');
		if (response.ok) {
			var productData = await response.json();
			
			var productTitle = productData.title ? productData.title.toLowerCase() : '';
			var productType = productData.product_type ? productData.product_type.toLowerCase() : '';
			var productTags = productData.tags ? productData.tags.join(' ').toLowerCase() : '';
			
			var isNullNull = productTitle.includes('null') || productType.includes('null') || 
			                 productTags.includes('null') || productTitle.includes('alternative');
			var isGinVodka = mode === 'gin' || mode === 'vodka';
			
			if (isGinVodka && isNullNull) {
				return null;
			}
			
			return productData;
		}
		
		throw new Error('Could not fetch product data');
	} catch (error) {
		return null;
	}
};

/**
 * Shows the variant alternatives popup
 * @param {Variant} selectedVariant - Currently selected variant
 * @param {VariantPicker|HTMLElement} variantPicker - Variant picker element
 * @param {string} mode - 'gin' or 'vodka'
 * @param {boolean} showBuyButton - Show buy button in popup
 */
window.showVariantAlternativesPopup = async function(selectedVariant, variantPicker, mode, showBuyButton) {
	try {
		var product = await window.getProductData(variantPicker, mode);
		
		if (!product || !product.variants || product.variants.length <= 1) {
			return;
		}

		var otherVariants = product.variants.filter(function(variant) {
			return variant.id !== selectedVariant.id && variant.available;
		});

		if (otherVariants.length === 0) {
			return;
		}

		var contentContainer = document.getElementById('variant-alternatives-content-' + mode);
		if (!contentContainer) {
			return;
		}

		contentContainer.innerHTML = '';

		otherVariants.forEach(function(variant) {
			var variantElement = window.createVariantElement(variant, variantPicker, mode, showBuyButton);
			contentContainer.appendChild(variantElement);
		});

		if (showBuyButton) {
			var proceedButton = document.createElement('div');
			proceedButton.className = 'variant-alternatives-popup__proceed';
			var popupRoot = document.getElementById('variant-alternatives-popup-' + mode);
			var proceedText = popupRoot ? popupRoot.getAttribute('data-proceed-button') : '';
			proceedButton.innerHTML = '<button type="button" class="konfigurator-nav__button konfigurator-nav__button--next" onclick="window.location.href = \'/cart\'">' + (proceedText || '') + '</button>';
			contentContainer.appendChild(proceedButton);
		}

		var titleElement = document.getElementById('variant-alternatives-title-' + mode);
		var noteElement = document.getElementById('variant-alternatives-note-' + mode);
		
		// Use translation strings provided via data attributes in markup; do not hardcode text
		// Expect data-title-buy / data-title-preview on title element and data-note-buy / data-note-preview on note element
		if (titleElement) {
			var titleBuy = titleElement.getAttribute('data-title-buy');
			var titlePreview = titleElement.getAttribute('data-title-preview');
			titleElement.textContent = showBuyButton ? (titleBuy || titleElement.textContent) : (titlePreview || titleElement.textContent);
		}
		if (noteElement) {
			var noteBuy = noteElement.getAttribute('data-note-buy');
			var notePreview = noteElement.getAttribute('data-note-preview');
			noteElement.textContent = showBuyButton ? (noteBuy || noteElement.textContent) : (notePreview || noteElement.textContent);
		}

		var popup = document.getElementById('variant-alternatives-popup-' + mode);
		if (popup) {
			if (popup.parentElement !== document.body) {
				document.body.appendChild(popup);
			}
			
			popup.classList.add('show');
			document.body.style.overflow = 'hidden';
			
			window.preventCartDrawerOpening();
			popup.setAttribute('data-show-buy-button', showBuyButton);

			// Backdrop click: always handle; mirrors Esc behavior
			popup.addEventListener('click', function(e) {
				if (e.target === popup) {
					if (showBuyButton) {
						window.proceedWithCurrentVariant(mode);
					} else {
						window.closeVariantAlternativesPopup(mode);
					}
				}
			}, { once: true });

			var escapeHandler = function(e) {
				if (e.key === 'Escape') {
					if (showBuyButton) {
						window.proceedWithCurrentVariant(mode);
					} else {
						window.closeVariantAlternativesPopup(mode);  
					}
					document.removeEventListener('keydown', escapeHandler);
				}
			};
			document.addEventListener('keydown', escapeHandler);
		}
	} catch (error) {
		// Silently handle errors
	}
};

// Global delegated handler for popup close button (data-action)
document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-action="closeVariantAlternativesPopup"]');
    if (!btn) return;
    var mode = btn.dataset.mode || getKonfiguratorMode();
    try { window.closeVariantAlternativesPopup(mode); } catch (_) {}
});

/**
 * Creates HTML element for a variant option
 * @param {Variant} variant - Variant object
 * @param {VariantPicker|HTMLElement} variantPicker - Variant picker element
 * @param {string} mode - 'gin' or 'vodka'
 * @param {boolean} showBuyButton - Show buy button
 * @returns {HTMLElement} Created variant element
 */
window.createVariantElement = function(variant, variantPicker, mode, showBuyButton) {
	var variantDiv = document.createElement('div');
	variantDiv.className = 'variant-alternatives-popup__variant' + (showBuyButton ? ' with-buy-option' : '');
	
	var formattedPrice = '';
	try {
		if (window.Shopify && window.Shopify.formatMoney && variant.price) {
			formattedPrice = window.Shopify.formatMoney(variant.price, window.theme ? window.theme.moneyFormat : '{{amount}}');
		} else if (variant.price) {
			var priceInEuros = variant.price / 100;
			formattedPrice = '€' + priceInEuros.toFixed(2);
		} else {
			formattedPrice = 'Price not available';
		}
	} catch (error) {
		formattedPrice = variant.price ? variant.price + ' cents' : 'Price not available';
	}

	var imageHtml = '';
	try {
		if (variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) {
			imageHtml = '<img src="' + variant.featured_media.preview_image.src + '" alt="' + (variant.title || 'Variant') + '" class="variant-alternatives-popup__variant-image">';
		} else if (variant.image && variant.image.src) {
			imageHtml = '<img src="' + variant.image.src + '" alt="' + (variant.title || 'Variant') + '" class="variant-alternatives-popup__variant-image">';
		}
	} catch (error) {
		// Silently handle image errors
	}

	var variantTitle = variant.title || variant.name || 'Variant ' + variant.id;
	var buyButtonHtml = '';

	if (showBuyButton) {
		// Read translated button label from popup root data attribute
		var popupRoot = document.getElementById('variant-alternatives-popup-' + mode);
		var chooseText = popupRoot ? popupRoot.getAttribute('data-choose-button') : '';
		buyButtonHtml = '<div class="variant-alternatives-popup__variant-buy"><button type="button" onclick="window.selectVariantAndPurchase(\'' + variant.id + '\', \'' + mode + '\')">' + (chooseText || '') + '</button></div>';
	}

	variantDiv.innerHTML = '<div class="variant-alternatives-popup__variant-header"><h4 class="variant-alternatives-popup__variant-name">' + variantTitle + '</h4><span class="variant-alternatives-popup__variant-price">' + formattedPrice + '</span></div>' + imageHtml + buyButtonHtml;

	if (!showBuyButton) {
		variantDiv.addEventListener('click', function() {
			window.selectVariantAndClosePopup(variant.id, variantPicker, mode);
		});
	}

	return variantDiv;
};

/**
 * Selects a variant and closes the popup (preview mode)
 * @param {string|number} variantId - Variant ID
 * @param {VariantPicker|HTMLElement} variantPicker - Variant picker element
 * @param {string} mode - 'gin' or 'vodka'
 */
window.selectVariantAndClosePopup = function(variantId, variantPicker, mode) {
	var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
	
	if (isMobile) {
		var previewInProgress = sessionStorage.getItem('preview-selection-in-progress');
		if (previewInProgress && (Date.now() - parseInt(previewInProgress)) < 500) {
			return;
		}
		sessionStorage.setItem('preview-selection-in-progress', Date.now().toString());
	}
	
	window.closeVariantAlternativesPopup(mode);
	
	if (window.innerWidth <= 768) {
		setTimeout(function() {
			var activeSection = document.querySelector('.konfigurator-' + mode + '-section.active, .konfigurator-' + mode + '-final.active');
			var sectionTitle = activeSection ? activeSection.querySelector('.konfigurator-' + mode + '-section__title') : null;
			if (sectionTitle) {
				sectionTitle.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}, 100);
	}
	
	try {
		var form = document.querySelector('form[action*="/cart/add"]');
		
		if (variantPicker && variantPicker.selectVariant) {
			variantPicker.selectVariant(variantId);
		}
		
		var variantIdInput = form ? form.querySelector('input[name="id"], select[name="id"]') : null;
		if (variantIdInput) {
			variantIdInput.value = variantId;
			variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
		}
		
		if (variantPicker && variantPicker.product) {
			var targetVariant = variantPicker.product.variants.find(function(v) { return v.id == variantId; });
			if (targetVariant) {
				targetVariant.options.forEach(function(optionValue, index) {
					var optionPosition = index + 1;
					
					var radioInput = variantPicker.querySelector('input[type="radio"][name="option' + optionPosition + '"][value="' + optionValue + '"]');
					if (radioInput) {
						radioInput.checked = true;
						radioInput.dispatchEvent(new Event('change', { bubbles: true }));
					}
					
					var selectInput = variantPicker.querySelector('select[name="option' + optionPosition + '"]');
					if (selectInput) {
						selectInput.value = optionValue;
						selectInput.dispatchEvent(new Event('change', { bubbles: true }));
					}
					
					var optionButton = variantPicker.querySelector('button[data-option-value="' + optionValue + '"]');
					if (optionButton) {
						optionButton.click();
					}
				});
			}
		}
		
		setTimeout(function() {
			if (form) {
				form.dispatchEvent(new CustomEvent('variant:change', {
					detail: { variant: { id: variantId } },
					bubbles: true
				}));
			}
			
			if (isMobile) {
				sessionStorage.removeItem('preview-selection-in-progress');
			}
		}, isMobile ? 150 : 100);
		
	} catch (error) {
		if (isMobile) {
			sessionStorage.removeItem('preview-selection-in-progress');
		}
	}
};

/**
 * Proceeds with current variant and adds to cart
 * @param {string} mode - 'gin' or 'vodka'
 */
window.proceedWithCurrentVariant = function(mode) {
	var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
	
	if (isMobile) {
		var proceedInProgress = sessionStorage.getItem('proceed-in-progress');
		if (proceedInProgress && (Date.now() - parseInt(proceedInProgress)) < 3000) {
			return;
		}
		sessionStorage.setItem('proceed-in-progress', Date.now().toString());
	}
	
	var popupShownKey = 'upselling-popup-shown-' + mode + '-' + window.location.pathname;
	sessionStorage.setItem(popupShownKey, 'true');
	
	window.closeVariantAlternativesPopup(mode);
	
	var form = document.querySelector('form[action*="/cart/add"]');
	if (form) {
		var buyButtons = form.querySelectorAll('button[name="add"], button[type="submit"], input[type="submit"]');
		buyButtons.forEach(function(button) {
			button.removeEventListener('click', window.handleBuyButtonClick, true);
		});
		
		var variantIdInput = form.querySelector('input[name="id"], select[name="id"]');
		
		if (variantIdInput && variantIdInput.value) {
			var formData = new FormData(form);
			var properties = {};
			for (var pair of formData.entries()) {
				if (pair[0].startsWith('properties[')) {
					properties[pair[0]] = pair[1];
				}
			}
			
			var hasKonfiguratorProperties = Object.keys(properties).some(function(key) {
				return key.includes('Rezept') || key.includes('Recipe') || 
				       key.includes('Alkohol') || key.includes('botanical') ||
				       key.includes('Botanical') || key.includes('begleiter');
			});
			
			if ((mode === 'gin' || mode === 'vodka') && !hasKonfiguratorProperties) {
				if (isMobile) {
					sessionStorage.removeItem('proceed-in-progress');
				}
				return;
			}
		}
		
		setTimeout(function() {
			form.submit();
			setTimeout(function() {
				window.location.href = '/cart';
			}, 500);
			if (isMobile) {
				setTimeout(function() {
					sessionStorage.removeItem('proceed-in-progress');
				}, 1000);
			}
		}, 100);
	} else {
		if (isMobile) {
			sessionStorage.removeItem('proceed-in-progress');
		}
		window.location.href = '/cart';
	}
};

/**
 * Selects variant and purchases (from popup buy button)
 * @param {string|number} variantId - Variant ID
 * @param {string} mode - 'gin' or 'vodka'
 */
window.selectVariantAndPurchase = function(variantId, mode) {
	try {
		var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
		
		if (isMobile) {
			var inProgress = sessionStorage.getItem('variant-selection-in-progress');
			if (inProgress && (Date.now() - parseInt(inProgress)) < 500) {
				return;
			}
			sessionStorage.setItem('variant-selection-in-progress', Date.now().toString());
		}
		
		var popupShownKey = 'upselling-popup-shown-' + mode + '-' + window.location.pathname;
		sessionStorage.setItem(popupShownKey, 'true');
		
		window.closeVariantAlternativesPopup(mode);
		
		if (window.innerWidth <= 768) {
			setTimeout(function() {
				var activeSection = document.querySelector('.konfigurator-' + mode + '-section.active, .konfigurator-' + mode + '-final.active');
				var sectionTitle = activeSection ? activeSection.querySelector('.konfigurator-' + mode + '-section__title') : null;
				if (sectionTitle) {
					sectionTitle.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			}, 100);
		}
		
		var variantPicker = document.querySelector('variant-picker');
		var form = document.querySelector('form[action*="/cart/add"]');
		
		if (variantPicker) {
			var expectedModes = ['gin', 'vodka'];
			var currentUrl = window.location.pathname.toLowerCase();
			var isKonfiguratorPage = expectedModes.some(function(m) { return currentUrl.includes(m) || currentUrl.includes('konfigurator'); });
			
			if (!isKonfiguratorPage) {
				if (isMobile) {
					sessionStorage.removeItem('variant-selection-in-progress');
				}
				return;
			}
			
			var mobileFormUpdated = false;
			if (isMobile) {
				var variantIdInput = form ? form.querySelector('input[name="id"], select[name="id"]') : null;
				if (variantIdInput) {
					variantIdInput.value = variantId;
					variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
					mobileFormUpdated = true;
				}
			}
			
			if (variantPicker.selectVariant) {
				variantPicker.selectVariant(variantId);
			}
			
			if (!mobileFormUpdated) {
				var variantIdInput = form ? form.querySelector('input[name="id"], select[name="id"]') : null;
				if (variantIdInput) {
					variantIdInput.value = variantId;
					variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
				}
			}
			
			if (window.currentVariantPicker && window.currentVariantPicker.product) {
				var targetVariant = window.currentVariantPicker.product.variants.find(function(v) { return v.id == variantId; });
				if (targetVariant) {
					targetVariant.options.forEach(function(optionValue, index) {
						var optionPosition = index + 1;
						var optionSelector = '[name="option' + optionPosition + '"]';
						
						var radioInput = variantPicker.querySelector('input[type="radio"]' + optionSelector + '[value="' + optionValue + '"]');
						if (radioInput) {
							radioInput.checked = true;
							radioInput.dispatchEvent(new Event('change', { bubbles: true }));
						}
						
						var selectInput = variantPicker.querySelector('select' + optionSelector);
						if (selectInput) {
							selectInput.value = optionValue;
							selectInput.dispatchEvent(new Event('change', { bubbles: true }));
						}
						
						var optionButton = variantPicker.querySelector('button[data-option-value="' + optionValue + '"]');
						if (optionButton) {
							optionButton.click();
						}
					});
				}
			}
			
			setTimeout(function() {
				if (form) {
					form.dispatchEvent(new CustomEvent('variant:change', {
						detail: { 
							variant: { id: variantId },
							productUrl: window.location.pathname
						},
						bubbles: true
					}));
				}
				
				if (isMobile) {
					sessionStorage.removeItem('variant-selection-in-progress');
				}
			}, isMobile ? 150 : 100);
			
		} else {
			if (!mobileFormUpdated) {
				var variantIdInput = form ? form.querySelector('input[name="id"], select[name="id"]') : null;
				if (variantIdInput) {
					variantIdInput.value = variantId;
					variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
				}
			}
			
			if (isMobile) {
				setTimeout(function() {
					sessionStorage.removeItem('variant-selection-in-progress');
				}, 100);
			}
		}
		
	} catch (error) {
		window.closeVariantAlternativesPopup(mode);
		
		var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
		if (isMobile) {
			sessionStorage.removeItem('variant-selection-in-progress');
		}
	}
};